import { queryOptions } from "@tanstack/react-query";
import type { Project } from "$/modules/appwrite/types/collections";
import { getProjectDashboardStats } from "$/modules/exam/lib/exam";
import { getProjectRole, listProjectCollaborators } from "./project-access";
import { getAllProjects, getLicenseStatus, getProjectById } from "./projects";

export const projectKeys = {
	account: (userId: string) => ["account", userId] as const,
	list: (userId: string) => ["account", userId, "projects"] as const,
	license: (userId: string) => ["account", userId, "license"] as const,
	project: (userId: string, projectId: string) =>
		["account", userId, "project", projectId] as const,
};

export const projectsQueryOptions = (userId: string) =>
	queryOptions({
		queryKey: projectKeys.list(userId),
		queryFn: () => getAllProjects(userId),
	});

export const licenseQueryOptions = (userId: string) =>
	queryOptions({
		queryKey: projectKeys.license(userId),
		queryFn: getLicenseStatus,
		staleTime: 60_000,
	});

export const projectQueryOptions = (userId: string, projectId: string) =>
	queryOptions({
		queryKey: [...projectKeys.project(userId, projectId), "detail"],
		queryFn: () => getProjectById({ projectId }),
	});

export const projectRoleQueryOptions = (userId: string, project: Project) =>
	queryOptions({
		queryKey: [...projectKeys.project(userId, project.$id), "role"],
		queryFn: () => getProjectRole(project, userId),
		enabled: Boolean(userId),
		throwOnError: (_error, query) => query.state.data === undefined,
	});

export const collaboratorsQueryOptions = (userId: string, projectId: string) =>
	queryOptions({
		queryKey: [...projectKeys.project(userId, projectId), "collaborators"],
		queryFn: () => listProjectCollaborators(projectId),
		enabled: Boolean(userId),
		throwOnError: (_error, query) => query.state.data === undefined,
	});

export const dashboardStatsQueryOptions = (userId: string, projectId: string) =>
	queryOptions({
		queryKey: [...projectKeys.project(userId, projectId), "stats"],
		queryFn: () => getProjectDashboardStats(projectId),
		staleTime: 15_000,
		throwOnError: (_error, query) => query.state.data === undefined,
	});
